import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { auth } from "../../firebase";
import PageHeader from "../PageHeader";
import FoodOrderMap from "./FoodOrderMap";
import { pageContentSx } from "../../pageLayout";
import {
  ORDER_STATUS,
  formatMoney,
  orderSteps,
  setOrderStatus,
  subscribeDeliveryCode,
  subscribeOrder,
  timestampMs,
} from "./foodUtils";

// Resumen de un pedido (lo ve el comprador y también el local).
export const OrderSummary = ({ order }) => (
  <>
    {order.items.map((item, index) => (
      <Box key={`${item.id}-${index}`} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">
            <strong>{item.qty}×</strong> {item.name}
          </Typography>
          {item.notes && <Typography variant="caption" color="text.secondary">{item.notes}</Typography>}
        </Box>
        <Typography variant="body2">{formatMoney(item.price * item.qty, order.currency)}</Typography>
      </Box>
    ))}
    <Divider sx={{ my: 1 }} />
    {order.mode === "delivery" && (
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">Envío</Typography>
        <Typography variant="body2">{order.deliveryFee ? formatMoney(order.deliveryFee, order.currency) : "Gratis"}</Typography>
      </Box>
    )}
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography sx={{ fontWeight: 800 }}>Total</Typography>
      <Typography sx={{ fontWeight: 800 }}>{formatMoney(order.total, order.currency)}</Typography>
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      {order.mode === "delivery" ? `Envío a ${order.address}` : "Retiro en el local"}
      {" · "}
      {order.paymentMethod !== "transferencia"
        ? "Efectivo"
        : ["pending", "rejected", "cancelled"].includes(order.status)
        ? "Transferencia"
        : `Transferencia: ${order.transferAlias}`}
    </Typography>
    {order.notes && (
      <Typography variant="body2" color="text.secondary">Comentarios: {order.notes}</Typography>
    )}
  </>
);

const FoodOrderStatus = () => {
  const { orderId } = useParams();
  const uid = auth.currentUser?.uid;
  const [order, setOrder] = useState(undefined);
  const [cancelling, setCancelling] = useState(false);
  const [deliveryCode, setDeliveryCode] = useState("");
  const isBuyer = !!order && order.buyerId === uid;

  useEffect(() => subscribeOrder(orderId, setOrder, () => setOrder(null)), [orderId]);
  useEffect(() => (isBuyer ? subscribeDeliveryCode(orderId, setDeliveryCode) : undefined), [orderId, isBuyer]);

  if (order === undefined) {
    return (
      <>
        <PageHeader title="Tu pedido" />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      </>
    );
  }
  if (!order) {
    return (
      <>
        <PageHeader title="Tu pedido" />
        <Box sx={{ ...pageContentSx, textAlign: "center", py: 8 }}>
          <Typography>No encontramos este pedido.</Typography>
          <Button component={Link} to="/pedidos" variant="contained" sx={{ mt: 2 }}>Volver a Pedidos</Button>
        </Box>
      </>
    );
  }

  const steps = orderSteps(order.mode);
  const finished = order.status === "rejected" || order.status === "cancelled";
  const activeStep = finished ? -1 : steps.indexOf(order.status) + (order.status === "delivered" ? 1 : 0);
  const createdMs = timestampMs(order.createdAt);

  const cancel = async () => {
    if (!window.confirm("¿Cancelar el pedido?")) return;
    setCancelling(true);
    try {
      await setOrderStatus(order.id, "cancelled");
    } catch (error) {
      console.error("No se pudo cancelar:", error);
      alert("El local ya tomó el pedido, no se puede cancelar desde acá.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <PageHeader title="Tu pedido" subtitle={order.restaurantName} />
      <Box sx={{ ...pageContentSx, maxWidth: { xs: "100%", md: 640 }, py: 2, pb: 6 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar src={order.restaurantLogo} sx={{ bgcolor: "primary.main" }}>🍴</Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }} noWrap>{order.restaurantName}</Typography>
              {createdMs > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(createdMs).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                </Typography>
              )}
            </Box>
            <Chip label={ORDER_STATUS[order.status]?.label} color={ORDER_STATUS[order.status]?.color || "default"} size="small" />
          </Box>

          {finished ? (
            <Alert severity={order.status === "rejected" ? "error" : "info"} sx={{ mt: 2 }}>
              {order.status === "rejected"
                ? "El local no pudo tomar tu pedido. Podés probar con otro local."
                : "Cancelaste este pedido."}
            </Alert>
          ) : (
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 3, mb: 1 }}>
              {steps.map((status) => (
                <Step key={status}>
                  <StepLabel>{status === "pending" ? "Recibido" : ORDER_STATUS[status].label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {order.status === "pending" && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
              Le avisamos al local. Te notificamos cuando lo acepte.
            </Typography>
          )}
          {order.paymentMethod === "transferencia" && !finished && order.status !== "delivered" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {order.status === "pending"
                ? "No transfieras todavía: cuando el local acepte el pedido, vas a ver acá el alias."
                : <>Transferí {formatMoney(order.total, order.currency)} a: <strong>{order.transferAlias}</strong></>}
            </Alert>
          )}
        </Paper>

        {isBuyer && deliveryCode && !finished && order.status !== "delivered" && (
          <Paper sx={{ p: 2, borderRadius: 3, mt: 2, textAlign: "center", bgcolor: "primary.main", color: "primary.contrastText" }}>
            <Typography variant="body2">Código de entrega</Typography>
            <Typography sx={{ fontSize: 44, fontWeight: 800, letterSpacing: 12, pl: "12px", lineHeight: 1.2 }}>
              {deliveryCode}
            </Typography>
            <Typography variant="body2">
              {order.mode === "delivery"
                ? "Decíselo al repartidor cuando te entregue el pedido. No lo compartas antes."
                : "Decíselo al local cuando retires el pedido."}
            </Typography>
          </Paper>
        )}

        {order.mode === "delivery" && !finished && order.status !== "delivered" && (order.deliveryLocation || order.restaurantLocation) && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Recorrido</Typography>
            <FoodOrderMap order={order} />
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Detalle</Typography>
          <OrderSummary order={order} />
        </Paper>

        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <Button variant="outlined" component={Link} to={`/pedidos/${order.restaurantId}`}>Ver el local</Button>
          {isBuyer && order.status === "pending" && (
            <Button color="error" variant="text" onClick={cancel} disabled={cancelling}>Cancelar pedido</Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default FoodOrderStatus;
